// src/services/apiService.ts
import axios from 'axios';
import { fetchPanels, fetchPanelsForSample, fetchPanel, getPanelTraits, PanelStatusFilter } from '../api/geneCodes/PanelApi';
import { fetchStagingRecords } from '../api/geneCodes/StagingApi';
import { fetchTrait } from '../api/geneCodes/TraitApi';
import { Panel, PanelTrait } from '../types/geneCodes/Panel';
import { Staging } from '../types/geneCodes/StagingTypes';
import { Trait } from '../types/geneCodes/TraitTypes';
import type { PatientSex } from '../utils/geneCodes/referenceRange';

/**
 * Fetches all available panels with optional filters
 */
export const getAllPanels = async (
  search?: string,
  modality?: string,
  panel_sex?: string,
  status?: PanelStatusFilter,
  withContent?: boolean
): Promise<Panel[]> => {
  try {
    const response = await fetchPanels(search, modality, panel_sex, true, status, withContent);
    return response.data;
  } catch (error) {
    console.error('Error fetching panels:', error);
    throw error;
  }
};

/**
 * Fetches the wheels one sample's rail may list
 */
export const getPanelsForSample = async (sampleId: string, sex?: PatientSex): Promise<Panel[]> => {
  try {
    const response = await fetchPanelsForSample(sampleId, sex);
    return response.data;
  } catch (error) {
    // 404 = the caller holds no auth_samples row for this sample: an empty rail, not a failure.
    if (axios.isAxiosError(error) && error.response?.status === 404) return [];
    console.error('Error fetching panels for sample:', error);
    throw error;
  }
};

/**
 * Fetches a single panel by ID with details
 */
export const getPanelById = async (id: number, includeHidden?: boolean): Promise<Panel> => {
  try {
    return await fetchPanel(id, includeHidden);
  } catch (error) {
    console.error(`Error fetching panel with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Fetches all traits for a specific panel
 */
export const getTraitsForPanel = async (
  panelId: number,
  search?: string,
  includeHidden?: boolean
): Promise<PanelTrait[]> => {
  try {
    return await getPanelTraits(panelId, search, undefined, undefined, includeHidden);
  } catch (error) {
    console.error(`Error fetching traits for panel ${panelId}:`, error);
    throw error;
  }
};

/**
 * Fetches SNPs for a specific trait within a panel
 */
export const getSnpsForTrait = async (
  traitName: string,
  page: number = 1,
  perPage: number = 100
): Promise<Staging[]> => {
  try {
    // Filter staging records by subcategory (trait)
    const response = await fetchStagingRecords(page, perPage, 'idstaging', 'asc', {
      subcategory: traitName
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching SNPs for trait ${traitName}:`, error);
    throw error;
  }
};

/**
 * Fetches SNPs for a specific gene
 */
export const getSnpsForGene = async (
  gene: string,
  page: number = 1,
  perPage: number = 100
): Promise<Staging[]> => {
  try {
    const response = await fetchStagingRecords(page, perPage, 'idstaging', 'asc', {
      gene: gene
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching SNPs for gene ${gene}:`, error);
    throw error;
  }
};

/**
 * Fetches all SNPs for a specific panel
 */
export const getAllSnpsForPanel = async (
  panelName: string,
  includeHidden?: boolean,
  page: number = 1,
  perPage: number = 200
): Promise<Staging[]> => {
  try {
    const first = await fetchStagingRecords(page, perPage, 'idstaging', 'asc', {
      category: panelName
    }, includeHidden);

    // Page rather than raise perPage — a silent cap is how wheel nodes disappeared in the first place.
    const loaded: Staging[] = [...first.data];
    let nextPage = page + 1;
    while (loaded.length < first.total && first.data.length > 0) {
      const next = await fetchStagingRecords(nextPage, perPage, 'idstaging', 'asc', {
        category: panelName
      }, includeHidden);
      if (next.data.length === 0) break;
      loaded.push(...next.data);
      nextPage++;
    }

    return loaded;
  } catch (error) {
    console.error(`Error fetching SNPs for panel ${panelName}:`, error);
    throw error;
  }
};

/**
 * Helper function to get trait detail by ID
 */
export const getTraitById = async (traitId: number): Promise<Trait> => {
  try {
    return await fetchTrait(traitId);
  } catch (error) {
    console.error(`Error fetching trait with ID ${traitId}:`, error);
    throw error;
  }
};